import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { publisherService } from "../services/publisherService";
import { publisherDisplayId } from "./PublisherCommonStore";
import { Publisher } from "../../../api/graphql";
import { countryDisplayIdByDemonym } from "../../country/store/CountryCommonStore";
import { countryDisplayId } from "../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../provinceState/store/ProvinceStateCommonStore";
import { getPublisherRepresentativeValues } from "../../publisherRepresentative/store/PublisherRepresentativeCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { getPublisherCollectingSocietyValues } from "../../publisherCollectingSociety/store/PublisherCollectingSocietyCommonStore";
import { CrudRows } from "../../common/types";
import { PersonType } from "../../../api/graphql";
import { MaritalStatus } from "../../../api/graphql";
import { countryService } from "../../country/services/countryService";
import { provinceStateService } from "../../provinceState/services/provinceStateService";
import { InitializeParams } from "../../../store/types";

export class PublisherViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = publisherService;
  publisherInstance: Publisher | null = null;
  cardState: Record<string, boolean> = {
    basicInformation: true,
    address: true,
    legalInformation: true,
    systemDates: false,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  personTypeValue: PersonType = "" as PersonType;
  nameValue: string = "";
  maritalStatusValue: MaritalStatus | "" = "" as MaritalStatus | "";
  nationalityService = countryService;
  nationalitySearchValue: string = "";
  nationalityIdValue: string = "";
  nationalitySelectedValue: { displayId: string; id: string } | null = null;
  pseudonymValue: string = "";
  taxIdValue: string = "";
  phoneValue: string = "";
  emailValue: string = "";
  streetAndNumberValue: string = "";
  floorValue: string = "";
  suiteValue: string = "";
  postalCodeValue: string = "";
  countryService = countryService;
  countrySearchValue: string = "";
  countryIdValue: string = "";
  countrySelectedValue: { displayId: string; id: string } | null = null;
  provinceService = provinceStateService;
  provinceSearchValue: string = "";
  provinceIdValue: string = "";
  provinceSelectedValue: { displayId: string; id: string } | null = null;
  cityValue: string = "";
  caeIpiValue: string = "";
  representativesColumnWidths: Record<string, number | string> = {};
  representativesRows: CrudRows = {};
  managementSocietiesColumnWidths: Record<string, number | string> = {};
  managementSocietiesRows: CrudRows = {};
  createdAt_Value: Date | null = null;
  lastUpdatedAt_Value: Date | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.representativesColumnWidths = { name: 200, role: 200 };
    this.managementSocietiesColumnWidths = {
      __collectingSociety: 200,
      cmsAccountNumber: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadPublisherById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.publisherOne({ id });
    this.publisherInstance = result.publisherOne;
    if (this.publisherInstance) {
      this.loadValues(this.publisherInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.publisherInstance) {
      return publisherDisplayId(this.publisherInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PublisherSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "nationality") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.countryOne({ id: params.returnData.resultId });
        const country = result.countryOne;
        this.nationalitySelectedValue = country;
      } else if (params.callerData?.fieldName === "country") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.countryOne({ id: params.returnData.resultId });
        const country = result.countryOne;
        this.countrySelectedValue = country;
      } else if (params.callerData?.fieldName === "province") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.provinceStateOne({
          id: params.returnData.resultId,
        });
        const provinceState = result.provinceStateOne;
        this.provinceSelectedValue = provinceState;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getPersonTypeOptions() {
    return this.langStore.PersonTypeOptions;
  }

  getMaritalStatusOptions() {
    return this.langStore.MaritalStatusOptions;
  }

  get nationalityValue() {
    if (this.nationalitySelectedValue) {
      return {
        id: this.nationalitySelectedValue.id,
        displayId: countryDisplayIdByDemonym(
          this.nationalitySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get countryValue() {
    if (this.countrySelectedValue) {
      return {
        id: this.countrySelectedValue.id,
        displayId: countryDisplayId(this.countrySelectedValue, this.langStore),
      };
    }
    return null;
  }

  get provinceValue() {
    if (this.provinceSelectedValue) {
      return {
        id: this.provinceSelectedValue.id,
        displayId: provinceStateDisplayId(
          this.provinceSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get representativesVisibleColumns() {
    return [
      {
        name: "name",
        title: this.langStore.PublisherRepresentativeNameLabel,
        width: this.representativesColumnWidths["name"],
      },
      {
        name: "role",
        title: this.langStore.PublisherRepresentativeRoleLabel,
        width: this.representativesColumnWidths["role"],
      },
    ];
  }

  representativesSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.representativesColumnWidths = values;
  }

  get representativesRowsDyn() {
    return this.representativesRows;
  }

  get managementSocietiesVisibleColumns() {
    return [
      {
        name: "__collectingSociety",
        title: this.langStore.PublisherCollectingSocietyCollectingSocietyLabel,
        width: this.managementSocietiesColumnWidths["__collectingSociety"],
      },
      {
        name: "cmsAccountNumber",
        title: this.langStore.PublisherCollectingSocietyCmsAccountNumberLabel,
        width: this.managementSocietiesColumnWidths["cmsAccountNumber"],
      },
    ];
  }

  managementSocietiesSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.managementSocietiesColumnWidths = values;
  }

  get managementSocietiesRowsDyn() {
    const entries = Object.entries(this.managementSocietiesRows).map(
      ([key, value]) => [
        key,
        {
          ...value,
          __collectingSociety: this.langStore.displayIdByLang(
            value.__collectingSociety
          ),
        },
      ]
    );
    return Object.fromEntries(entries);
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.publisherInstance = null;
    this.personTypeValue = "" as PersonType;
    this.nameValue = "";
    this.maritalStatusValue = "" as MaritalStatus | "";
    this.nationalitySearchValue = "";
    this.nationalityIdValue = "";
    this.pseudonymValue = "";
    this.taxIdValue = "";
    this.phoneValue = "";
    this.emailValue = "";
    this.streetAndNumberValue = "";
    this.floorValue = "";
    this.suiteValue = "";
    this.postalCodeValue = "";
    this.countrySearchValue = "";
    this.countryIdValue = "";
    this.provinceSearchValue = "";
    this.provinceIdValue = "";
    this.cityValue = "";
    this.caeIpiValue = "";
    this.representativesRows = {};
    this.managementSocietiesRows = {};
    this.createdAt_Value = null;
    this.lastUpdatedAt_Value = null;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelPublisher() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("publisher_update");
  }

  editPublisher() {
    const id = this.publisherInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/publisher/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printPublisher(handlePrint: () => void): void {
    handlePrint();
  }

  *closePublisher(): any {
    yield this.close();
  }

  loadValues(instance: Publisher) {
    this.idValue = instance.id;
    this.personTypeValue = instance.personType;
    this.nameValue = instance.name;
    this.maritalStatusValue =
      instance.maritalStatus || ("" as MaritalStatus | "");
    this.nationalitySelectedValue = instance.nationality
      ? instance.nationality
      : null;
    this.pseudonymValue = instance.pseudonym || "";
    this.taxIdValue = instance.taxId;
    this.phoneValue = instance.phone;
    this.emailValue = instance.email;
    this.streetAndNumberValue = instance.streetAndNumber;
    this.floorValue = instance.floor || "";
    this.suiteValue = instance.suite || "";
    this.postalCodeValue = instance.postalCode;
    this.countrySelectedValue = instance.country;
    this.provinceSelectedValue = instance.province;
    this.cityValue = instance.city;
    this.caeIpiValue = instance.caeIpi;
    this.representativesRows = instance.representatives
      ? Object.assign(
          {},
          ...instance.representatives.map((x) => {
            const processedRow = getPublisherRepresentativeValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.managementSocietiesRows = instance.managementSocieties
      ? Object.assign(
          {},
          ...instance.managementSocieties.map((x) => {
            const processedRow = getPublisherCollectingSocietyValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.createdAt_Value = new Date(instance.createdAt_);
    this.lastUpdatedAt_Value = new Date(instance.lastUpdatedAt_);
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadPublisherById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
